import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Button,
  useTheme,
  Divider
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';

const AIChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (messages.length === 0) {
      // Welcome message
      setMessages([
        {
          id: 1,
          type: 'bot',
          content: `Hello! I'm your Alzheimer's Care Assistant. I'm here to help answer questions about Alzheimer's disease, symptoms, diagnosis, treatment options, and caregiving.

What would you like to know about Alzheimer's disease today?`,
          timestamp: new Date(),
        },
      ]);
    }
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    if (!isAuthenticated) {
      setError('Please sign up to use the chat feature.');
      return;
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');

    try {
      const response = await api.post('/chat', {
        message: inputMessage,
        conversationHistory: messages.slice(-5), // Send last 5 messages for context
      });

      const data = response.data;

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError('');
    // Re-add welcome message
    setMessages([
      {
        id: 1,
        type: 'bot',
        content: `Hello! I'm your Alzheimer's Care Assistant. I'm here to help answer questions about Alzheimer's disease, symptoms, diagnosis, treatment options, and caregiving.

What would you like to know about Alzheimer's disease today?`,
        timestamp: new Date(),
      },
    ]);
  };

  const formatTimestamp = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const quickQuestions = [
    "What are the early signs of Alzheimer's?",
    "How is Alzheimer's diagnosed?",
    "What treatments are available?",
    "How can I care for someone with Alzheimer's?",
    "What causes Alzheimer's disease?",
    "Is there a cure for Alzheimer's?",
  ];

  const handleQuickQuestion = (question) => {
    setInputMessage(question);
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning" sx={{ mb: 4 }}>
          <AlertTitle>Sign Up Required</AlertTitle>
          Please sign up to use the AI chat feature. Create your account to start chatting with our AI assistant.
        </Alert>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button variant="contained" onClick={() => navigate('/signup')} sx={{ mr: 2 }}>
            Sign Up
          </Button>
          <Button variant="outlined" onClick={() => navigate('/signin')}>
            Sign In
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="info" sx={{ mb: 4 }}>
        <AlertTitle>Important Notice</AlertTitle>
        This information is for educational purposes only and should not replace professional medical advice. 
        Always consult with healthcare providers for personalized diagnosis and treatment.
      </Alert>

      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
            <BotIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              AI Healthcare Assistant
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
              AI healthcare support powered by Google Gemini 2.5 Flash
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          height: '70vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            backgroundColor: '#f8f9fa',
          }}
        >
          {messages.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Start a conversation about Alzheimer's disease
              </Typography>
            </Box>
          )}

          <List sx={{ p: 0 }}>
            {messages.map((message) => (
              <ListItem
                key={message.id}
                sx={{
                  display: 'flex',
                  flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <ListItemAvatar
                  sx={{
                    minWidth: 40,
                    ...(message.type === 'user' && { ml: 1, mr: 0 }),
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: message.type === 'user' ? theme.palette.secondary.main : theme.palette.primary.main,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {message.type === 'user' ? <PersonIcon /> : <BotIcon />}
                  </Avatar>
                </ListItemAvatar>
                <Box
                  sx={{
                    maxWidth: '75%',
                    ...(message.type === 'user' && { textAlign: 'right' }),
                  }}
                >
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      bgcolor: message.type === 'user' ? theme.palette.primary.light : 'white',
                      color: message.type === 'user' ? 'white' : '#212121',
                      borderRadius: 2,
                      ...(message.type === 'user' && {
                        borderBottomRightRadius: 4,
                      }),
                      ...(message.type === 'bot' && {
                        borderBottomLeftRadius: 4,
                      }),
                    }}
                  >
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-line',
                        color: message.type === 'user' ? 'white' : '#212121',
                        fontWeight: message.type === 'bot' ? 400 : 'normal'
                      }}
                    >
                      {message.content}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 0.5,
                        fontSize: '0.75rem',
                        color: message.type === 'user' 
                          ? 'rgba(255, 255, 255, 0.85)' 
                          : '#616161',
                        fontWeight: 400
                      }}
                    >
                      {formatTimestamp(message.timestamp)}
                    </Typography>
                  </Paper>
                </Box>
              </ListItem>
            ))}
          </List>

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                Thinking...
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ m: 1 }}>
              {error}
              <Button size="small" onClick={() => setError('')} sx={{ ml: 1 }}>
                Dismiss
              </Button>
            </Alert>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Quick Questions */}
        {messages.length <= 1 && (
          <Box sx={{ p: 2, backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Quick questions:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {quickQuestions.map((question, index) => (
                <Chip
                  key={index}
                  label={question}
                  size="small"
                  clickable
                  onClick={() => handleQuickQuestion(question)}
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider />

        {/* Input Area */}
        <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask about Alzheimer's symptoms, treatments, care..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              size="medium"
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading}
              color="primary"
              size="large"
              sx={{ 
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                }
              }}
            >
              <SendIcon />
            </IconButton>
            <IconButton
              onClick={clearChat}
              size="medium"
              color="secondary"
              title="Clear chat"
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default AIChat;

